import type { AIAnalysisRequest } from '$lib/types/ai';
import { KOREAN_GRAMMAR_SYSTEM_PROMPT, JSON_FORMAT_INSTRUCTION } from './GrammarConstants';

/**
 * Language options for prompt formatting
 */
export type PromptLanguage = 'en' | 'ko';

/**
 * Grammar-specific prompt builder for AI providers
 */
export class GrammarPromptBuilder {
  /**
   * Build system prompt for Korean grammar analysis
   */
  static buildSystemPrompt(includeJsonFormat: boolean = true): string {
    if (includeJsonFormat) {
      return `${KOREAN_GRAMMAR_SYSTEM_PROMPT}\n\n${JSON_FORMAT_INSTRUCTION}`;
    }
    return KOREAN_GRAMMAR_SYSTEM_PROMPT;
  }

  /**
   * Format user prompt for correction analysis
   */
  static formatUserPrompt(request: AIAnalysisRequest, language: PromptLanguage = 'ko'): string {
    const contextLabel = language === 'en' ? 'Context' : '컨텍스트';
    const errorsLabel = language === 'en' ? 'Errors to correct' : '교정할 오류들';
    const optionsLabel = language === 'en' ? 'Options' : '선택지';
    const helpLabel = language === 'en' ? 'Help' : '도움말';
    const correctionCount = request.corrections.length;

    let prompt = `${contextLabel}: "${request.contextText}"\n\n${errorsLabel} (TOTAL: ${correctionCount}):\n`;

    request.corrections.forEach((correction: any, index: number) => {
      const original = correction.original || '';
      const corrected = Array.isArray(correction.corrected)
        ? correction.corrected.join(', ')
        : (correction.corrected || '');

      prompt += `${index + 1}. "${original}" → ${optionsLabel}: [${corrected}]\n`;
      if (correction.help) {
        prompt += `   ${helpLabel}: ${correction.help}\n`;
      }
    });

    // Add critical instruction
    const criticalInstruction = language === 'en'
      ? `\nCRITICAL: You MUST provide exactly ${correctionCount} entries in your response. Each error above MUST have a corresponding correction entry. DO NOT skip any errors.`
      : `\n중요: 응답에 정확히 ${correctionCount}개의 항목을 제공해야 합니다. 위의 각 오류마다 해당하는 교정 항목이 있어야 합니다. 오류를 건너뛰지 마세요.`;

    prompt += criticalInstruction;

    return prompt;
  }

  /**
   * Build OpenAI-style message array
   */
  static buildOpenAIMessages(request: AIAnalysisRequest, language: PromptLanguage = 'en'): any[] {
    return [
      {
        role: 'system',
        content: this.buildSystemPrompt()
      },
      {
        role: 'user',
        content: this.formatUserPrompt(request, language)
      }
    ];
  }

  /**
   * Build Anthropic-style single prompt
   */
  static buildAnthropicPrompt(request: AIAnalysisRequest, language: PromptLanguage = 'ko'): string {
    const systemPrompt = this.buildSystemPrompt();
    const userPrompt = this.formatUserPrompt(request, language);
    return `${systemPrompt}\n\n${userPrompt}`;
  }

  /**
   * Build Google-style prompt without JSON format instruction (uses structured output)
   */
  static buildGooglePrompt(request: AIAnalysisRequest, language: PromptLanguage = 'ko'): string {
    const systemPrompt = `${KOREAN_GRAMMAR_SYSTEM_PROMPT}

각 오류에 대해 다음 정보를 제공해주세요:
- correctionIndex: 오류의 인덱스 번호 (0부터 시작)
- selectedValue: 선택된 수정안 또는 더 나은 대안 (keepOriginal이 true이면 원본 텍스트를 입력)
- isExceptionProcessed: 예외 처리 여부 (고유명사, URL 등)
- keepOriginal: 원본이 수정 제안어보다 더 적절한 경우 반드시 true로 설정 (중요!)
- reasoning: 선택 이유 설명

중요 규칙:
- 원본 텍스트가 올바르다고 판단되면 반드시 keepOriginal을 true로 설정하세요
- keepOriginal이 true일 때는 selectedValue에 원본 텍스트를 그대로 입력하세요
- 맞춤법 검사기가 잘못된 제안을 한 경우, keepOriginal을 true로 설정하세요`;

    const userPrompt = this.formatUserPrompt(request, language);
    return `${systemPrompt}\n\n${userPrompt}`;
  }

  /**
   * Build Upstage-style message array with Korean emphasis
   */
  static buildUpstageMessages(request: AIAnalysisRequest): any[] {
    const correctionCount = request.corrections.length;

    const systemPrompt = `당신은 한국어 문법 전문가입니다. 주어진 맞춤법 검사 결과를 분석하여 가장 적절한 교정을 선택하고, 그 이유를 설명해야 합니다.

중요: 모든 설명과 이유(reasoning)는 반드시 한국어로 작성하세요. 영어로 응답하지 마세요.
IMPORTANT: All explanations and reasoning MUST be in Korean, NOT in English.

규칙:
1. 각 교정에 대해 제공된 선택지 중 하나를 선택하거나, 원문 유지를 선택
2. 전문적이고 학술적인 문맥을 고려
3. 상황에 따른 예외 처리 필요성 판단
4. 자연스러운 문장 흐름 고려
5. reasoning 필드는 반드시 한국어로 작성

**CRITICAL - keepOriginal 필드 사용법:**
- reasoning에서 "원본이 맞다", "원본이 적절하다", "원본이 올바르다"라고 판단했다면 반드시 keepOriginal을 true로 설정
- 맞춤법 검사기의 제안이 잘못되었다고 판단했다면 반드시 keepOriginal을 true로 설정
- keepOriginal이 true일 때는 selectedValue에 원본 텍스트를 그대로 입력
- 절대로 reasoning에서는 원본이 맞다고 하면서 keepOriginal을 false로 설정하지 마세요

예시:
- "검사기" → 제안: "검사비" → reasoning: "원본이 맞음" → keepOriginal: true, selectedValue: "검사기" ✓
- "검사기" → 제안: "검사비" → reasoning: "원본이 맞음" → keepOriginal: false ✗ (절대 금지!)

응답 형식은 반드시 JSON 배열이어야 합니다:
[
  {
    "correctionIndex": 0,
    "selectedValue": "선택한 값",
    "isExceptionProcessed": false,
    "keepOriginal": false,
    "reasoning": "한국어로 작성된 선택 이유"
  }
]

중요: 응답에 정확히 ${correctionCount}개의 항목을 제공해야 합니다. 위의 각 오류마다 해당하는 교정 항목이 있어야 합니다. 오류를 건너뛰지 마세요.`;

    let userPrompt = `컨텍스트: "${request.contextText}"

교정할 오류들 (TOTAL: ${correctionCount}) (모든 설명은 한국어로 작성해주세요):
`;

    request.corrections.forEach((correction: any, index: number) => {
      const original = correction.original || '';
      const corrected = Array.isArray(correction.corrected)
        ? correction.corrected.join(', ')
        : (correction.corrected || '');

      userPrompt += `${index + 1}. "${original}" → 선택지: [${corrected}]
`;
      if (correction.help) {
        userPrompt += `   도움말: ${correction.help}
`;
      }
    });

    userPrompt += `\n중요: 응답에 정확히 ${correctionCount}개의 항목을 제공해야 합니다. 각 오류마다 해당하는 교정 항목이 있어야 합니다. 오류를 건너뛰지 마세요.`;

    return [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ];
  }
}